/**
 * Indexing notice — a dismissible "the library is still growing" announcement
 * shown at the top of the idle scene. It lets visitors know the corpus is being
 * actively indexed, so an answer that comes up short or a source that isn't found
 * today may resolve in the coming days.
 *
 * Dismissal is saved in sessionStorage (namespaced `bow-and-arrow:*`, matching
 * state/session-persistence.ts): once dismissed it stays gone for the tab session,
 * and it returns on a fresh session. Informational (walnut/gold) — deliberately
 * distinct from the red <ErrorBanner>. Self-contained: reads the store once on
 * mount, writes on dismiss; degrades to "shown, non-persistent" if storage throws
 * (private mode). No live region — it's static page content, read in flow.
 */
import { useCallback, useState } from "react";
import type { Strings } from "../strings";
import type { LatestBook } from "../chat/use-latest-book";
import { corpusPath } from "../corpus/use-corpus-route";

const DISMISS_KEY = "bow-and-arrow:indexing-notice-dismissed";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false; // storage unavailable → show the notice (fail open)
  }
}

export interface IndexingNoticeProps {
  strings: Strings;
  /** Newest book in the corpus, or null while loading / unavailable. */
  latestBook: LatestBook | null;
  onOpenBook: (book: string, chapter: number) => void;
}

export function IndexingNotice({
  strings,
  latestBook,
  onOpenBook,
}: IndexingNoticeProps): JSX.Element | null {
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const handleDismiss = useCallback((): void => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* dismissal still applies for this session's render; just not persisted */
    }
  }, []);

  if (dismissed) return null;

  return (
    <aside className="indexing-notice">
      {/* The notice card proper — unchanged. The newest-book panel is a SIBLING
          below it, not part of it, so the card keeps its original shape. */}
      <div className="indexing-notice__card">
        <span className="indexing-notice__pulse" aria-hidden="true" />
        <div className="indexing-notice__body">
          <p className="indexing-notice__lead">{strings.noticeIndexingLead}</p>
          <p className="indexing-notice__text">{strings.noticeIndexingBody}</p>
        </div>
        <button
          type="button"
          className="indexing-notice__dismiss"
          onClick={handleDismiss}
          aria-label={strings.noticeDismissAria}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
            <path
              d="M3.5 3.5l7 7M10.5 3.5l-7 7"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      {latestBook !== null && (
        /* A real <a href> so middle-click / cmd-click / "copy link" behave
           natively; the click handler only takes over for plain left-clicks,
           to keep SPA navigation. */
        <a
          className="indexing-notice__latest"
          href={corpusPath(latestBook.slug, latestBook.firstChapter)}
          aria-label={strings.noticeLatestAria(latestBook.name)}
          onClick={(event) => {
            if (event.defaultPrevented) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            if (event.button !== 0) return;
            event.preventDefault();
            onOpenBook(latestBook.slug, latestBook.firstChapter);
          }}
        >
          <span className="indexing-notice__latest-text">
            <span className="indexing-notice__latest-kicker">{strings.noticeLatestLabel}</span>
            <span className="indexing-notice__latest-line">
              <span className="indexing-notice__latest-name">{latestBook.name}</span>
              <span className="indexing-notice__latest-count">
                {strings.noticeLatestChapters(latestBook.chapters)}
              </span>
            </span>
          </span>
          <svg
            className="indexing-notice__latest-arrow"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M3 7.5h8M7.6 4.1l3.4 3.4-3.4 3.4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </a>
      )}
    </aside>
  );
}
